import {
  Component,
  signal,
  inject,
  OnInit,
  effect,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { MENU_ITEMS, MenuItem, SubMenuItem } from './menu.config';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import {
  IconComponent,
  type IconName,
} from '../../shared/icons/icon.component';
import { PrismLogoComponent } from '../../shared/components/prism-logo/prism-logo.component';
import { filter } from 'rxjs/operators';
import {
  trigger,
  state,
  style,
  transition,
  animate,
  query,
  stagger,
  group,
} from '@angular/animations';
import {
  DropdownComponent,
  DropdownOption,
} from '../../shared/components/ui/dropdown/dropdown.component';
import { ApiService } from '../../core/services/api.service';
import { UnpaidPaymentsAccessService } from '../../pages/private/unpaid-payments-v2/unpaid-payments-access.service';
import { UnpaidPaymentsHubOverviewService } from '../../pages/private/unpaid-payments-v2/unpaid-payments-hub-overview.service';
import { Subscription } from 'rxjs';
import { SidebarService } from '../../core/services/sidebar.service';
import { IconComponentV2 } from '../../shared/components/icon-component/icon.component';
import { isRegisteredComponent } from '../../core/registry/component-registry';
import { isMenuRoute } from '../../core/services/menu-route-match';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PrismLogoComponent,
    DropdownComponent,
    IconComponentV2
  ],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    // Fast, smooth submenu animation to prevent flickering
    trigger('submenuAnimation', [
      transition(':enter', [
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
          transform: 'translateY(-5px)',
        }),
        animate(
          '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({
            height: '*',
            opacity: 1,
            transform: 'translateY(0)',
          }),
        ),
      ]),
      transition(':leave', [
        style({
          overflow: 'hidden',
        }),
        animate(
          '150ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({
            height: '0px',
            opacity: 0,
            transform: 'translateY(-3px)',
          }),
        ),
      ]),
    ]),

    // Simplified item animation
    trigger('submenuItems', [
      transition(':enter', [
        query(
          ':scope > *',
          [
            style({
              opacity: 0,
              transform: 'translateX(-8px)',
            }),
            stagger(40, [
              animate(
                '150ms 50ms ease-out',
                style({
                  opacity: 1,
                  transform: 'translateX(0)',
                }),
              ),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),

    // Side card animation for collapsed mode
    trigger('sideCardSlide', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(-20px) scale(0.95)',
          transformOrigin: 'left center',
        }),
        animate(
          '250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          style({
            opacity: 1,
            transform: 'translateX(0) scale(1)',
          }),
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({
            opacity: 0,
            transform: 'translateX(-15px) scale(0.96)',
          }),
        ),
      ]),
    ]),

    // Side card items animation
    trigger('sideCardItems', [
      transition(':enter', [
        query(
          ':scope > *',
          [
            style({
              opacity: 0,
              transform: 'translateX(-12px)',
            }),
            stagger(30, [
              animate(
                '180ms 100ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                style({
                  opacity: 1,
                  transform: 'translateX(0)',
                }),
              ),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private sidebarService = inject(SidebarService);
  private router = inject(Router);
  private subscription!: Subscription;
  apiservice: ApiService = inject(ApiService);
  private readonly unpaidAccessService = inject(UnpaidPaymentsAccessService);
  private readonly unpaidHubOverviewService = inject(UnpaidPaymentsHubOverviewService);
  productsList = signal<DropdownOption[]>([]);
  isCollapsed = this.sidebarService.isCollapsed;
  hoveredItem = signal<MenuItem | null>(null);
  defaultProduct = signal<any>(null);
  private readonly isRefresh = !!this.router.url && this.router.url !== '/';

  constructor() {
    effect(() => {
      if (!this.apiservice.getSelectedProductId()) {
        this.apiservice.setSelectedProductId(this.defaultProduct());
      }
    });

    effect(() => {
      this.unpaidAccessService.probedCount();
      this.unpaidAccessService.probingComplete();
      this.unpaidAccessService.unauthorizedProducts();
      this.applyUnpaidAccessToMenu();
    });
  }

  ngOnInit(): void {
    // Use setTimeout to ensure router is fully initialized
    setTimeout(() => {
      this.updateActiveStateFromRoute(this.router.url);
    });

    // Listen to route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateActiveStateFromRoute(event.url);
      });

    this.loadProducts();
    this.subscription = this.sidebarService.triggerMethod$.subscribe(() => {
      // this.loadProducts();
      this.onProductChange(
        this.apiservice.getSelectedProductId() || this.defaultProduct(),
      );
    });
  }

  ngOnDestroy() {
    // Always unsubscribe to prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  menuItems: MenuItem[] = MENU_ITEMS;

  toggleCollapse() {
    this.sidebarService.toggleCollapse();
    // Auto-collapse all expanded items when sidebar is collapsed
    if (this.isCollapsed()) {
      this.menuItems.forEach((item) => {
        if (item.expanded) item.expanded = false;
      });
    }
  }

  toggleExpanded(item: MenuItem) {
    if (!item.children) return;
    if (this.isCollapsed()) return;

    // Collapse all other menus first
    for (const menu of this.menuItems) {
      if (menu !== item) {
        menu.expanded = false;
      }
    }

    // Toggle the clicked item
    item.expanded = !item.expanded;
  }

  setActiveItem(clickedItem: MenuItem) {
    this.menuItems.forEach((item) => {
      item.active = item === clickedItem;
      // Reset children active states
      if (item.children) {
        item.children.forEach((child) => (child.active = false));
      }
    });
  }

  setActiveSubItem(parentItem: MenuItem, clickedSubItem: SubMenuItem) {
    // Set parent as active
    this.menuItems.forEach((item) => {
      item.active = item === parentItem;
      if (item.children) {
        item.children.forEach((child) => {
          child.active = child === clickedSubItem;
        });
      }
    });
  }

  hasActiveChild(item: MenuItem): boolean {
    if (!item) return false;
    if (item.active) return true;
    if (!item.children || !Array.isArray(item.children)) return false;
    return item.children.some((child) => child && child.active === true);
  }

  isItemActive(item: MenuItem): boolean {
    return Boolean(item?.active);
  }

  hasVisibleChildren(item: MenuItem): boolean {
    return !!item.children && item.children.some((child) => !child.hidden);
  }

  isSubItemActive(subItem: SubMenuItem): boolean {
    return Boolean(subItem?.active);
  }

  isParentOrChildActive(item: MenuItem): boolean {
    return this.isItemActive(item) || this.hasActiveChild(item);
  }

  loadProducts(): void {
    if (this.productsList().length > 0) return;

    this.apiservice.fetchProducts().subscribe({
      next: (response: any) => {
        const stored = sessionStorage.getItem('dynamic_routes');
        const dynamicRoutes = stored ? JSON.parse(stored) : [];

        const allowedProductIds = dynamicRoutes
          .map((item: any) => item.product?.id)
          .filter(Boolean);

        const filteredProducts = response
          .filter(
            (item: any) => item.active && allowedProductIds.includes(item.id),
          )
          .map((item: any) => ({
            value: item.id,
            label: item.name,
          }));

        this.productsList.set(filteredProducts);

        // RESTORE FROM URL
        const productIdFromUrl = this.getProductIdFromUrl();

        if (productIdFromUrl) {
          this.defaultProduct.set(productIdFromUrl);
          this.apiservice.setSelectedProductId(productIdFromUrl);
          this.syncSelectedProductPid(productIdFromUrl);
          this.loadMenuByProductId(productIdFromUrl);
          return;
        }

        // RESTORE FROM SESSION (e.g. mobile reload on a shared route path)
        const storedProductId = sessionStorage.getItem('selected_product_id');
        const isAllowedProduct = (id: string | null) =>
          !!id && filteredProducts.some((p: { value: string }) => p.value === id);

        if (isAllowedProduct(storedProductId)) {
          this.defaultProduct.set(storedProductId);
          this.apiservice.setSelectedProductId(storedProductId);
          this.loadMenuByProductId(storedProductId!);
          return;
        }

        // FIRST LOGIN → Default Denefits
        const denefits = filteredProducts.find(
          (p: { label: string }) => p.label.toLowerCase() === 'denefits',
        );

        if (denefits) {
          this.defaultProduct.set(denefits.value);
          this.syncSelectedProductPid(denefits.value);
          this.loadMenuByProductId(denefits.value);
        } else if (filteredProducts.length > 0) {
          const firstProduct = filteredProducts[0];
          this.defaultProduct.set(firstProduct.value);
          this.syncSelectedProductPid(firstProduct.value);
          this.loadMenuByProductId(firstProduct.value);
        }
      },
      error: (error: any) => console.error('Error fetching products:', error),
    });
  }

  /**
   * Resolve the numeric `pid` for the given product cuid `id` and push it
   * onto `apiService.selectedProductPid` so consumers (e.g. My Dashboard's
   * `/dashboards?...&product_id=<pid>` call) read the correct value.
   *
   * Lookup priority mirrors `getProductIdFromUrl()` (uses `dynamic_routes`)
   * and falls back to `apiService.products()` so the value resolves even if
   * `dynamic_routes` is missing on first paint.
   */
  private syncSelectedProductPid(productId: string | null): void {
    if (!productId) {
      this.apiservice.setSelectedProductPid(null);
      return;
    }

    let pid: string | number | null = null;

    try {
      const stored = sessionStorage.getItem('dynamic_routes');
      if (stored) {
        const allProducts = JSON.parse(stored) as any[];
        const matchedRoute = allProducts.find(
          (p: any) => String(p?.product?.id) === String(productId),
        );
        if (matchedRoute?.product?.pid != null) {
          pid = matchedRoute.product.pid;
        }
      }
    } catch (err) {
      console.warn('[Sidebar] Failed to parse dynamic_routes for pid lookup:', err);
    }

    if (pid == null) {
      const products = this.apiservice.products();
      const matched = products.find(
        (p: any) => String(p?.id) === String(productId),
      );
      pid = matched?.pid ?? null;
    }

    this.apiservice.setSelectedProductPid(pid);
  }

  private updateActiveStateFromRoute(url: string): void {
    // Strip query params for matching
    const currentPath = url.split('?')[0];

    // Reset all active states
    this.menuItems.forEach((item) => {
      item.active = false;
      item.expanded = false;
      if (item.children) {
        item.children.forEach((child) => (child.active = false));
      }
    });

    // Special handling for dashboard routes
    if (
      currentPath === '/app/dashboard' ||
      currentPath.startsWith('/app/dashboard')
    ) {
      const dashboardItem = this.menuItems.find(
        (item) => item.route === '/app/dashboard',
      );
      if (dashboardItem) {
        dashboardItem.active = true;
        dashboardItem.expanded = true;

        if (dashboardItem.children) {
          // Find exact matching child or default to first child
          const matchingChild = dashboardItem.children.find(
            (child) => child.route === currentPath,
          );
          if (matchingChild) {
            matchingChild.active = true;
          } else {
            // Default to first child for /app/dashboard
            dashboardItem.children[0].active = true;
          }
        }
        return;
      }
    }

    // Handle other routes
    for (const item of this.menuItems) {
      if (item.route && currentPath === item.route) {
        item.active = true;
        break;
      }

      // Check children routes
      if (item.children) {
        for (const child of item.children) {
          if (child.route && currentPath === child.route) {
            item.active = true;
            item.expanded = true;
            child.active = true;
            return;
          }
        }
      }
    }
  }

  // Hover side card methods
  sideCardPosition = signal<{ top: number; left: number }>({
    top: 0,
    left: 80,
  });
  private hideTimeout: any = null;

  onItemHover(item: MenuItem, element?: HTMLElement): void {
    if (this.isCollapsed() && item.children && item.children.length > 0) {
      // Clear any pending hide timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }

      this.hoveredItem.set(item);

      if (element) {
        const rect = element.getBoundingClientRect();
        this.sideCardPosition.set({
          top: rect.top,
          left: 80, // Fixed left position for collapsed sidebar (5rem = 80px)
        });
      }
    }
  }

  onItemLeave(): void {
    // Use a longer delay to allow moving to the side card
    this.hideTimeout = setTimeout(() => {
      this.hoveredItem.set(null);
    }, 300);
  }

  onSideCardHover(): void {
    // Clear any pending hide timeout when hovering over the side card
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  onSideCardLeave(): void {
    // Immediately hide when leaving the side card
    this.hoveredItem.set(null);
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  onProductChange(event: any) {
    const productId = event;

    if (!productId) {
      console.warn('No productId found in event');
      return;
    }

    // Store selected product in API service
    this.apiservice.setSelectedProductId(productId);
    // Resolve & expose the numeric `pid` for this product so the Dashboard
    // API call (`/dashboards?...&product_id=<pid>`) uses the right value.
    this.syncSelectedProductPid(productId);

    this.loadMenuByProductId(productId);
  }

  loadMenuByProductIds(productId: string) {
    const stored = sessionStorage.getItem('dynamic_routes');

    if (!stored) {
      console.warn('No dynamic_routes found in sessionStorage');
      this.menuItems = [];
      return;
    }

    const allProducts = JSON.parse(stored);

    const selectedProduct = allProducts.find(
      (p: any) => p.product?.id === productId,
    );

    if (!selectedProduct?.routes?.length) {
      console.warn('No routes available for this product');
      this.menuItems = [];
      return;
    }

    const toAppRoute = (path: string): string => {
      if (!path) return '/app';
      return path.startsWith('/app')
        ? path
        : '/app/' + path.replace(/^\/+/, '');
    };

    this.menuItems = selectedProduct.routes.map((item: any, index: number) => {
      const staticMenu = MENU_ITEMS.find((m) => m.label === item.name);

      return {
        label: item.name,
        route: item.path ? toAppRoute(item.path) : null,
        icon: this.resolveMenuIcon(item, staticMenu),
        expanded: false,
        active: false,

        children: (item.subMenus || []).map((sub: any, subIndex: number) => {
          return {
            label: sub.name,
            route: sub.path ? toAppRoute(sub.path) : null,
            active: false,
          };
        }),
      };
    });

    this.applyMenuIconFallbacks();
    this.ensureUnpaidAccessProbing();
    this.applyUnpaidAccessToMenu();

    const firstParent = this.menuItems[0];
    const firstChild = firstParent?.children?.[0];

    if (firstChild?.route) {
      this.router.navigateByUrl(firstChild.route);
    } else if (firstParent?.route) {
      this.router.navigateByUrl(firstParent.route);
    }

    this.updateActiveStateFromRoute(this.router.url);
  }

  loadMenuByProductId(productId: string) {
    const stored = sessionStorage.getItem('dynamic_routes');
    if (!stored) {
      this.menuItems = [];
      return;
    }

    const allProducts = JSON.parse(stored);

    const selectedProduct = allProducts.find(
      (p: any) => p.product?.id === productId,
    );

    if (!selectedProduct?.routes?.length) {
      this.menuItems = [];
      return;
    }

    const toAppRoute = (path: string): string => {
      if (!path) return '/app';
      return path.startsWith('/app')
        ? path
        : '/app/' + path.replace(/^\/+/, '');
    };

    this.menuItems = selectedProduct.routes
      // Only show routes that are sidebar-visible AND actually renderable
      // (a leaf needs its component registered; a parent needs a renderable child).
      .filter(
        (item: any) => item.onlyPath === false && this.isRouteRenderable(item)
      )
      .map((item: any) => {
        const staticMenu = MENU_ITEMS.find((m) => m.label === item.name);

        const children = (item.subMenus || [])
          .filter((sub: any) => this.isRouteRenderable(sub))
          .map((sub: any) => ({
            id: sub.id,
            label: sub.name,
            route: sub.path ? toAppRoute(sub.path) : null,
            active: false,
            hidden: sub.onlyPath === true,
          }));

        return {
          id: item.id,
          label: item.name,
          route: item.path ? toAppRoute(item.path) : null,
          icon: this.resolveMenuIcon(item, staticMenu),
          expanded: false,
          active: false,
          children,
        };
      });

    this.applyMenuIconFallbacks();
    this.ensureUnpaidAccessProbing();
    this.applyUnpaidAccessToMenu();

    const currentUrl = this.router.url;
    // Strip query params for matching
    const currentPath = currentUrl.split('?')[0];

    // const isCurrentRouteValid = this.menuItems.some(
    //   (item) =>
    //     item.route === currentUrl ||
    //     item.children?.some((child) => child.route === currentUrl)
    // );

    const isCurrentRouteValid = isMenuRoute(selectedProduct.routes, currentPath);

    // ✅ REFRESH / DEEP LINK CASE
    if (isCurrentRouteValid) {
      this.updateActiveStateFromRoute(currentUrl);
      return;
    }

    // ✅ FIRST TIME ENTRY ONLY
    const firstParent = this.menuItems[0];
    const firstChild = firstParent?.children?.[0];

    const targetRoute = firstChild?.route || firstParent?.route;

    if (targetRoute) {
      this.router.navigateByUrl(targetRoute).then(() => {
        this.updateActiveStateFromRoute(targetRoute);
      });
    }
  }

  private menuHasUnpaidPaymentsRoute(): boolean {
    return this.menuItems.some(
      (item) =>
        (item.route && item.route.includes('/unpaid-payments')) ||
        item.children?.some((child) => child.route?.includes('/unpaid-payments'))
    );
  }

  private isUnpaidPaymentsMenuItem(item: {
    name?: string;
    path?: string;
    label?: string;
    route?: string | null;
  }): boolean {
    const label = (item.name ?? item.label ?? '').toLowerCase().trim();
    const path = (item.path ?? item.route ?? '').toLowerCase();
    return label.includes('unpaid payments') || path.includes('unpaid-payments');
  }

  /**
   * Mirrors buildDynamicRoutesFromApi: a leaf route is only navigable when its
   * component is registered; a parent route is navigable when it has at least
   * one renderable descendant. Non-renderable routes get no Angular route, so
   * they must not appear in the sidebar.
   */
  private isRouteRenderable(route: any): boolean {
    if (route?.subMenus?.length) {
      return route.subMenus.some((sub: any) => this.isRouteRenderable(sub));
    }
    return isRegisteredComponent(route?.componentName);
  }

  private resolveMenuIcon(
    item: { name?: string; path?: string; icon?: string | null },
    staticMenu?: MenuItem | null
  ): IconName {
    const apiIcon = typeof item?.icon === 'string' ? item.icon.trim() : '';
    if (apiIcon) {
      return apiIcon as IconName;
    }

    if (staticMenu?.icon) {
      return staticMenu.icon;
    }

    if (this.isUnpaidPaymentsMenuItem(item)) {
      return 'circleDollarSign' as IconName;
    }

    return 'layoutDashboard' as IconName;
  }

  private applyMenuIconFallbacks(): void {
    for (const item of this.menuItems) {
      if (item.icon) {
        continue;
      }

      if (this.isUnpaidPaymentsMenuItem(item)) {
        item.icon = 'circleDollarSign' as IconName;
        continue;
      }

      item.icon = 'layoutDashboard' as IconName;
    }
  }

  private ensureUnpaidAccessProbing(): void {
    if (this.menuHasUnpaidPaymentsRoute() && !this.unpaidAccessService.probingComplete()) {
      this.unpaidHubOverviewService.loadCards();
    }
  }

  private productKeyFromMenuRoute(route: string | null): string | null {
    if (!route) {
      return null;
    }
    const segment = route.split('/').filter(Boolean).pop() ?? '';
    return this.unpaidAccessService.productKeyForRouteSegment(segment);
  }

  private applyUnpaidAccessToMenu(): void {
    for (const parent of this.menuItems) {
      if (!parent.children?.length) {
        continue;
      }

      for (const child of parent.children) {
        const productKey =
          this.unpaidAccessService.productKeyForSidebarLabel(child.label) ??
          this.productKeyFromMenuRoute(child.route ?? null);

        if (!productKey) {
          continue;
        }

        child.disabled =
          !this.unpaidAccessService.isProbed(productKey) ||
          !this.unpaidAccessService.hasAccess(productKey);
      }
    }
  }

  private getProductFromUrl(): string | null {
    const url = this.router.url;
    // example: /app/lendee/lendee-dashboard/xyz

    const match = url.match(/^\/app\/([^\/]+)/);
    return match ? match[1] : null;
  }

  private getProductIdFromUrl(): string | null {
    const stored = sessionStorage.getItem('dynamic_routes');
    if (!stored) return null;

    const currentUrl = typeof window !== 'undefined' ? window.location.pathname : this.router.url;
    if (!currentUrl || currentUrl === '/' || currentUrl === '/app') return null;

    try {
      const allProducts = JSON.parse(stored);

      const urlMatch = currentUrl.match(/^\/app\/([^\/]+)/);
      if (!urlMatch) return null;

      const productBasePath = urlMatch[1];

      const matchedProduct = allProducts.find((p: any) => {
        const basePath = p.product?.basePath;
        if (!basePath) return false;

        const cleanBasePath = basePath.split('/').filter(Boolean).pop();
        return cleanBasePath === productBasePath;
      });
      const pid = matchedProduct?.product?.pid || null;
      return matchedProduct?.product?.id || null;
    } catch (error) {
      console.error('Error parsing dynamic routes:', error);
      return null;
    }
  }
}
